/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import classNames from 'classnames';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import { Controller } from 'react-hook-form';
import { FormControl, FormControlLabel, makeStyles } from '@material-ui/core';
import { Trans, useTranslation } from 'react-i18next';
import Tooltip from '../../components/common/Tooltip';
import Button from '../../components/common/Button';
import { CustomSelectBox } from '../../components/common/CustomSelectBox';
import useEPCreationDialog from './useEPCreationDialog';
import CustomCheckbox from '../../components/CustomCheckbox';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { resolveDataProtectionLink } from '../../shared/utils/i18nUtils';

const useStyles = makeStyles(theme => ({
  ...theme.customTheme,
  checkboxContainer: {
    marginTop: '2rem',
    marginLeft: '0.5rem',
  },
  checkbox: {
    '& a': {
      position: 'absolute',
      width: '150px',
      paddingLeft: '4px',
      zIndex: 2,
    },
  },
  form: {
    display: 'flex',
    paddingTop: '5px',
    flexDirection: 'column',
    marginTop: '2rem',
    [theme.breakpoints.down('md')]: {
      marginTop: '1.5rem',
    },
  },
  field: {
    position: 'relative',
    marginRight: '.1rem',
    marginBottom: '3rem',
    paddingRight: '1rem',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      marginBottom: '1rem',
    },
  },
  epLanguageField: {
    display: 'flex',
    alignItems: 'center',
  },
  tooltip: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    right: '-24px',
  },
}));

export default function EPCreationDialog({ createEPDialog }) {
  const classes = useStyles();
  const { i18n } = useTranslation();
  const frontendCustomisationConfig = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);
  // Hide the checkbox from the EP1001 dialog if the bussiness requierd this
  // Add showCheckboxDialogEP1001 to frontendCustomisation.json file in fo-configuration
  // Asign showCheckboxDialogEP1001 to false to hide the checkbox
  const showCheckbox = frontendCustomisationConfig?.resource?.showCheckboxDialogEP1001;
  const dataProtectionLink = resolveDataProtectionLink(frontendCustomisationConfig?.resource, i18n.language);
  const {
    paymentMethodsList,
    defaultPaymentMethod,
    languageList,
    defaultLanguage,
    userReferenceRules,
    control,
    handleSubmit,
    errors,
    formState,
    t,
  } = useEPCreationDialog();

  return (
    <Dialog open={createEPDialog.isOpen} data-testid="ep-creation-dialog" fullWidth maxWidth="md">
      <DialogTitle>
        <Typography component="h2" variant="h2">
          {t('submission.ep.createDialog.title')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" component="span" gutterBottom>
          {t('submission.ep.createDialog.description')}
        </Typography>
        <form
          id="createEPform"
          onSubmit={handleSubmit(createEPDialog.confirm)}
          className={classNames(classes.form, 'rjsf')}
        >
          <Box display="grid">
            <Controller
              control={control}
              name="fileReference"
              rules={userReferenceRules}
              defaultValue=""
              render={({ onChange, value, name }) => (
                <div className={classes.field}>
                  <TextField
                    name={name}
                    multiline
                    label={t('userReference')}
                    value={value}
                    onChange={onChange}
                    error={!!errors[name]}
                    fullWidth
                  />
                  <FormHelperText component="p" error>
                    {errors[name]?.message}
                  </FormHelperText>
                  {frontendCustomisationConfig?.resource?.showEPToolTip && (
                    <Tooltip
                      className={classes.tooltip}
                      title={t('submission.ep.createDialog.userReference.tooltip')}
                    />
                  )}
                </div>
              )}
            />
            <CustomSelectBox
              name="paymentMethods"
              control={control}
              itemId="itemId"
              itemLabel="itemLabel"
              items={paymentMethodsList}
              label={t('submission.ep.createDialog.paymentMethod')}
              defaultValue={defaultPaymentMethod || ''}
              className={classes.field}
              validationRules={{ required: true }}
            />
            <div className={classes.epLanguageField}>
              <CustomSelectBox
                name="requestLanguage"
                control={control}
                itemId="itemId"
                itemLabel="itemLabel"
                items={languageList}
                label={t('submission.ep.createDialog.language.label')}
                defaultValue={defaultLanguage || ''}
                validationRules={{ required: true }}
              />
              <Tooltip title={t('submission.ep.createDialog.language.tooltip')} />
            </div>
            {showCheckbox !== false && (
              <div className={classes.checkboxContainer}>
                <Controller
                  name="dataProtectionAcceptance"
                  control={control}
                  rules={{ required: true }}
                  defaultValue={false}
                  render={({ onChange }) => (
                    <FormControl>
                      <FormControlLabel
                        className={classes.checkbox}
                        label={(
                          <Trans i18nKey="basic-filing.data-protection-ep1001-with-link">
                            <a href={dataProtectionLink} target="_blank" rel="noreferrer">
                              dummy content
                            </a>
                          </Trans>
                        )}
                        control={<CustomCheckbox onChange={e => onChange(e.target.checked)} />}
                      />
                    </FormControl>
                  )}
                />
              </div>
            )}
          </Box>
        </form>
      </DialogContent>
      <DialogActions>
        <Button id="cancelEPCTCreate" color="default" onClick={createEPDialog.close}>
          {t('actions.cancel')}
        </Button>
        <Button id="confirmEPCreate" form="createEPform" color="primary" type="submit" disabled={!formState?.isValid}>
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
